import { useState, useEffect, useCallback, useRef, type ReactNode } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import { ArrowRight, ArrowUpRight, MoreHorizontal, X } from "lucide-react"
import { BOOK_DEMO_PATH } from "@/lib/cta"
import { trackBookDemoClick } from "@/lib/openaiAds"

const LIGHT_SECTION_IDS: string[] = []
const DARK_OVERRIDE_IDS = ['features']

const NAV_SECTIONS = [
  { name: 'Features', id: 'features' },
  { name: 'How It Works', id: 'how-it-works' },
  { name: 'FAQs', id: 'faqs' },
]

const EXPLORE_LINKS = [
  { name: 'Football', href: '/sports/football' },
  { name: 'Basketball', href: '/sports/basketball' },
  { name: 'Padel', href: '/sports/padel' },
  { name: 'Communities', href: '/solutions/communities' },
  { name: 'Academies', href: '/solutions/academies' },
  { name: 'Venues', href: '/solutions/venues' },
]

function ExploreMenu({ linkText, activeLinkText }: { linkText: string; activeLinkText: string }) {
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const current = EXPLORE_LINKS.some((item) => location.pathname === item.href)

  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onPointer)
    }
  }, [open])

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((value) => !value)}
        className={`rounded-full px-3.5 py-1.5 text-[13px] font-medium min-h-[40px] ${current ? activeLinkText : linkText}`}
      >
        Explore
      </button>
      {open ? (
        <div className="absolute left-0 top-[calc(100%+10px)] z-[120] min-w-[220px] rounded-2xl border border-white/10 bg-[#0c0c0c] p-2 shadow-[0_16px_40px_-12px_rgba(0,0,0,0.7)]">
          {EXPLORE_LINKS.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className={`flex min-h-11 items-center rounded-xl px-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#71cdd9] ${
                location.pathname === item.href ? 'text-white bg-white/10' : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              {item.name}
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  )
}

const MOBILE_SPORTS = EXPLORE_LINKS.slice(0, 3)
const MOBILE_SOLUTIONS = EXPLORE_LINKS.slice(3)

function MobileMenuGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="px-1 pt-3 first:pt-1">
      <p className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/45">{title}</p>
      {children}
    </div>
  )
}

function MobileMenu({ onSection }: { onSection: (id: string) => void }) {
  const location = useLocation()
  const reduceMotion = useReducedMotion()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      buttonRef.current?.focus()
    }
    const onPointer = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer)
    }
  }, [open])

  const chip = (active: boolean) =>
    `flex min-h-11 items-center justify-center rounded-xl px-2 text-[13px] font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[#71cdd9] ${
      active ? 'bg-white/[0.14] text-white' : 'bg-white/[0.06] text-white/85 active:bg-white/[0.12]'
    }`

  return (
    <div ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls="mobile-site-menu"
        aria-label={open ? 'Close menu' : 'Open menu'}
        onClick={() => setOpen((value) => !value)}
        className="flex h-10 w-10 items-center justify-center rounded-full bg-white/[0.08] text-white transition-colors active:bg-white/[0.16] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#71cdd9]"
      >
        {open ? <X className="h-5 w-5" aria-hidden /> : <MoreHorizontal className="h-5 w-5" aria-hidden />}
      </button>

      <AnimatePresence>
        {open ? (
          <motion.nav
            id="mobile-site-menu"
            aria-label="Site"
            initial={{ opacity: 0, y: reduceMotion ? 0 : -8, scale: reduceMotion ? 1 : 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: reduceMotion ? 0 : -6 }}
            transition={{ duration: 0.18, ease: [0.25, 1, 0.5, 1] }}
            className="absolute left-0 right-0 top-[calc(100%+8px)] z-[120] origin-top rounded-[22px] border border-white/10 bg-[#0c0c0c] p-2 pb-3 text-white shadow-[0_20px_48px_-16px_rgba(0,0,0,0.85)]"
          >
            <MobileMenuGroup title="On this page">
              <div className="flex flex-col">
                {NAV_SECTIONS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      setOpen(false)
                      onSection(item.id)
                    }}
                    className="flex min-h-11 items-center rounded-xl px-2 text-left text-[15px] font-medium text-white/90 active:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#71cdd9]"
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </MobileMenuGroup>

            <MobileMenuGroup title="Sports">
              <div className="grid grid-cols-3 gap-1.5">
                {MOBILE_SPORTS.map((item) => (
                  <Link key={item.href} to={item.href} className={chip(location.pathname === item.href)}>
                    {item.name}
                  </Link>
                ))}
              </div>
            </MobileMenuGroup>

            <MobileMenuGroup title="Solutions">
              <div className="grid grid-cols-3 gap-1.5">
                {MOBILE_SOLUTIONS.map((item) => (
                  <Link key={item.href} to={item.href} className={chip(location.pathname === item.href)}>
                    {item.name}
                  </Link>
                ))}
              </div>
            </MobileMenuGroup>

            <div className="mx-3 mt-3 border-t border-white/10 pt-1">
              <Link
                to={BOOK_DEMO_PATH}
                onClick={trackBookDemoClick}
                className="-mx-1 flex min-h-11 items-center justify-between rounded-xl px-1 text-[15px] font-medium text-white/90 active:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#71cdd9]"
              >
                Contact us
                <ArrowRight className="h-4 w-4 text-white/50" aria-hidden />
              </Link>
              <Link
                to="/docs/api"
                target="_blank"
                rel="noopener noreferrer"
                className="-mx-1 flex min-h-11 items-center justify-between rounded-xl px-1 text-[15px] font-medium text-white/90 active:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#71cdd9]"
              >
                API docs
                <ArrowUpRight className="h-4 w-4 text-white/50" aria-hidden />
              </Link>
            </div>
          </motion.nav>
        ) : null}
      </AnimatePresence>
    </div>
  )
}

const MobileLoginButton = () => {
  return (
    <Link
      to="/login"
      className="inline-flex items-center rounded-full px-3.5 py-1.5 text-sm font-semibold text-black transition-transform duration-200 hover:scale-[1.03] touch-manipulation min-h-[40px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#71cdd9]"
      style={{ backgroundColor: '#71cdd9' }}
    >
      Log in
    </Link>
  )
}

function useHeaderOverLightSection() {
  const [isLight, setIsLight] = useState(false)
  useEffect(() => {
    const check = () => {
      const threshold = 100
      let overLight = false
      for (const id of LIGHT_SECTION_IDS) {
        const el = document.getElementById(id)
        if (!el) continue
        const rect = el.getBoundingClientRect()
        if (rect.top <= threshold && rect.bottom > 0) { overLight = true; break }
      }
      let overDarkOverride = false
      for (const id of DARK_OVERRIDE_IDS) {
        const el = document.getElementById(id)
        if (!el) continue
        const rect = el.getBoundingClientRect()
        if (rect.top <= threshold && rect.bottom > 0) { overDarkOverride = true; break }
      }
      setIsLight(overLight && !overDarkOverride)
    }
    check()
    window.addEventListener('scroll', check, { passive: true })
    window.addEventListener('resize', check)
    return () => { window.removeEventListener('scroll', check); window.removeEventListener('resize', check) }
  }, [])
  return isLight
}

const SECTION_TO_NAV: Record<string, string> = {
  'home': 'features',
  'features': 'features',
  'how-it-works': 'how-it-works',
  'faqs': 'faqs',
}

function useHideOnScrollDown() {
  const [hidden, setHidden] = useState(false)

  useEffect(() => {
    let lastY = window.scrollY
    let ticking = false

    const update = () => {
      const currentY = window.scrollY

      if (currentY < 50) {
        setHidden(false)
      } else if (currentY > lastY && currentY > 100) {
        setHidden(true)
      } else if (currentY < lastY) {
        setHidden(false)
      }

      lastY = currentY
      ticking = false
    }

    const onScroll = () => {
      if (!ticking) {
        ticking = true
        window.requestAnimationFrame(update)
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return hidden
}

function useActiveSection() {
  const [active, setActive] = useState<string>('features')

  useEffect(() => {
    const detect = () => {
      const scrollY = window.scrollY
      const viewportH = window.innerHeight
      const midPoint = scrollY + viewportH * 0.35

      const sectionIds = ['home', 'features', 'how-it-works', 'faqs']
      let best = 'features'

      for (const id of sectionIds) {
        const el = document.getElementById(id)
        if (!el) continue
        const rect = el.getBoundingClientRect()
        const elTop = scrollY + rect.top
        if (elTop <= midPoint) {
          best = SECTION_TO_NAV[id] || id
        }
      }
      setActive(best)
    }

    detect()
    window.addEventListener('scroll', detect, { passive: true })
    return () => window.removeEventListener('scroll', detect)
  }, [])

  return active
}

export const Header = () => {
  const isOverLightSection = useHeaderOverLightSection()
  const activeSection = useActiveSection()
  const navHidden = useHideOnScrollDown()

  const location = useLocation()
  const navigate = useNavigate()
  const isHomePage = location.pathname === '/'
  const isBookDemo = location.pathname === '/book-demo'

  const scrollToSection = useCallback((id: string) => {
    if (isHomePage) {
      const element = document.getElementById(id)
      if (element) element.scrollIntoView({ behavior: 'smooth' })
    } else {
      navigate('/', { state: { scrollTo: id } })
    }
  }, [isHomePage, navigate])

  const pillBase = isOverLightSection
    ? 'bg-black/[0.04] border-black/[0.08] text-black/70'
    : 'bg-white/[0.05] border-white/[0.1] text-white/80'
  const linkText = isOverLightSection ? 'text-black/60 hover:text-black' : 'text-white/65 hover:text-white'
  const activeLinkText = isOverLightSection ? 'text-black' : 'text-white'
  const activePillBg = isOverLightSection ? 'bg-black/[0.07]' : 'bg-white/[0.1]'

  return (
    <header
      className="fixed top-0 left-0 right-0 z-[100] pointer-events-auto md:pointer-events-none will-change-transform isolate"
      style={{
        transform: navHidden ? 'translateY(-120%)' : 'translateY(0)',
        transition: 'transform 300ms ease-in-out',
      }}
    >
      <div className="pt-3 sm:pt-4 px-5 sm:px-4 md:pointer-events-none">
        {/* Desktop: floating glass pill with logo + nav links only */}
        <div className="hidden md:flex justify-center pointer-events-none">
          <div
            className={`pointer-events-auto inline-flex items-center gap-1.5 rounded-full border backdrop-blur-xl shadow-[0_8px_24px_-8px_rgba(0,0,0,0.4)] transition-colors duration-200 pl-3 pr-2 py-1.5 ${pillBase}`}
          >
            <Link
              to="/"
              onClick={(e) => {
                if (isHomePage) { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }) }
              }}
              className="flex items-center gap-1.5 shrink-0 pr-2.5 mr-1 border-r border-current/10"
            >
              <img src="/Logo_Statbox_Transparent.png" alt="Statbox" className="h-5 w-auto" />
              <img
                src="/logo_white.png"
                alt="Statbox"
                className={`h-2.5 w-auto transition-[filter] duration-300 ${isOverLightSection ? 'invert' : ''}`}
              />
            </Link>

            <ExploreMenu linkText={linkText} activeLinkText={activeLinkText} />

            {!isBookDemo && NAV_SECTIONS.map((item) => {
              const isActive = isHomePage && activeSection === item.id
              return (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`relative rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-colors ${
                    isActive ? activeLinkText : linkText
                  }`}
                >
                  {isActive && (
                    <motion.span
                      layoutId="nav-active-pill"
                      className={`absolute inset-0 rounded-full ${activePillBg}`}
                      transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                    />
                  )}
                  <span className="relative z-10">{item.name}</span>
                </button>
              )
            })}

            <Link
              to="/docs/api"
              target="_blank"
              rel="noopener noreferrer"
              className={`relative rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-colors ${linkText}`}
            >
              API Docs
            </Link>

            <Link
              to="/login"
              className="relative z-10 ml-1 inline-flex items-center rounded-full px-3.5 py-1.5 text-[13px] font-semibold text-black transition-transform duration-200 hover:scale-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#71cdd9]"
              style={{ backgroundColor: '#71cdd9' }}
            >
              Log in
            </Link>
          </div>
        </div>

        {/* Mobile: floating pill with logo + log in */}
        <div className="md:hidden flex justify-center">
          <div
            className={`relative z-10 flex items-center justify-between gap-1 rounded-full border backdrop-blur-md pl-4 pr-1.5 py-1.5 mt-1 mx-auto w-full max-w-[calc(100%-2rem)] touch-manipulation ${
              isOverLightSection
                ? 'bg-black/[0.04] border-black/[0.08]'
                : 'bg-white/[0.05] border-white/10'
            }`}
          >
            <Link
              to="/"
              onClick={(e) => {
                if (isHomePage) { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }) }
              }}
              className="flex items-center gap-1.5 min-h-[40px] touch-manipulation pr-1 shrink-0"
            >
              <img src="/Logo_Statbox_Transparent.png" alt="Statbox" className="h-5 w-auto" />
              <img
                src="/logo_white.png"
                alt="Statbox"
                className={`h-2.5 w-auto transition-[filter] duration-300 ${isOverLightSection ? 'invert' : ''}`}
              />
            </Link>

            <div className="flex items-center gap-1.5">
              <MobileLoginButton />
              <MobileMenu onSection={scrollToSection} />
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}
